import React, { useMemo, useEffect } from "react";
import useFilterQuery from "../../../../hooks/react-query/brandPerformance/useFilterQuery";
import { RootStateOrAny, useSelector } from "react-redux";
import { performancePages } from "../../../../constants";
import { isNullOrUndefined, toLocaleInteger } from "../../../../utils/utilityFunctions";
import { getBrandVolumeTrend, getBrandVolumeAverage } from "../../../../api/brandPerformanceApi";
import { RouteComponentProps, withRouter } from "react-router-dom";
import BrandPerformanceTrendTile from "../BrandPerformanceTrendTile";
import BrandPerformanceTableTile from "../BrandPerformanceTableTile";

interface Props extends RouteComponentProps {
  onHandleRadioClick: any;
  nissanCompetitor: string;
  activePage: performancePages;
  diff: number;
  granularity: string;
}

const TableTooltipParagraphs = () => (
  <>
    <p>
      <span style={{ fontWeight: "bold" }}>Volume average </span>
      considers the sum of all brand/model sales in the select period dividided by the number of months in the period.
    </p>
    <p>
      <u>Source</u>: Total (Retail & Fleet) Sales Data by PAI
    </p>
    <br />
  </>
);

const TrendTooltipParagraph = () => (
  <>
    <p>
      <span style={{ fontWeight: "bold" }}>Volume </span>
      represents the total sales of a brand or car model in the selected geography in the select period of time. Competitors corporate and
      model data are shown based on the selected Nissan brand/model.
    </p>
    <p>
      <u>Source</u>: Total (Retail & Fleet) Sales Data by PAI
    </p>
    <br />
  </>
);

const BrandVolume = withRouter((props: Props) => {
  const { onHandleRadioClick, nissanCompetitor, activePage, diff, history, granularity } = props;

  const params = useSelector((state: RootStateOrAny) => state.parameters);
  const { date_range } = params;
  const [onSettleCount, setOnSettleCount] = React.useState(0);
  const [shouldRenderDataComponents, setShouldRenderDataComponents] = React.useState(false);

  // fetch the trend and average data
  const {
    data: chartData,
    isFetching: loadingTrend, // call data using react-query
    refetch: refetchVolumeTrend,
  } = useFilterQuery(
    ["brandVolumeTrend", granularity, history.location.search],
    () => getBrandVolumeTrend({ ...params, nissan_competitor: nissanCompetitor }),
    params,
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  const {
    data: tableData,
    isFetching: loadingTable,
    refetch: refetchBrandVolumeAverage,
  } = useFilterQuery(
    ["brandVolumeAverage", granularity, history.location.search],
    () => getBrandVolumeAverage({ ...params, nissan_competitor: nissanCompetitor }),
    params,
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // when the url changes, refetch the data
  useEffect(() => {
    refetchVolumeTrend();
    refetchBrandVolumeAverage();
  }, [history.location.search]);

  const model = params.model;

  const dateRange = tableData?.date_range ?? [];

  const columns = [
    {
      id: "model",
      Header: `${model == "Brand" ? "Brand" : "Model"} `,
      accessor: "model",
      sortType: "alphanumericIgnoreCase",
      Cell: (props1: { value: string }) => {
        const { value } = props1;

        return value;
      },
    },
    {
      Header: "Average Monthly Volume",
      accessor: `volume`,
      id: "volume",
      sortType: "alphanumericCustom",

      Cell: (props: { cell: { row: { original: any } } }) => {
        const {
          cell: {
            row: { original },
          },
        } = props;
        const value = original?.["volume"];
        return !isNullOrUndefined(value) ? toLocaleInteger(value) : "n/a";
      },
    },
  ];

  const dateTitle = useMemo(() => {
    if (dateRange && dateRange.length > 1) {
      return `(${dateRange[0]} - ${dateRange.slice(-1)})`;
    } else if (dateRange && dateRange.length == 1) {
      return `(${dateRange[0]})`;
    }

    return "";
  }, [dateRange]);

  return (
    <div className="brand-market-main-content">
      <BrandPerformanceTrendTile
        title={`${model} Volume Trend`}
        chartId="marketShareTrend"
        chartData={chartData}
        loadingTrend={loadingTrend}
        diff={diff}
        TrendTooltipParagraph={TrendTooltipParagraph}
        nissanCompetitor={nissanCompetitor}
        onHandleRadioClick={onHandleRadioClick}
        chartGranularity={granularity}
      />
      <BrandPerformanceTableTile
        title={`${model} Volume Average`}
        tableId="brand_market_share_table"
        tableData={tableData}
        columns={columns}
        loadingTable={loadingTable}
        initialSortCol={{ id: "volume", desc: true }}
        TableTooltipParagraphs={TableTooltipParagraphs}
      />
    </div>
  );
});

export default BrandVolume;
